import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Header from "../../../../components/Header/Header";
import IndentDetails from "./IndentDetails";
import ItemsTable from "./ItemsTable";
import IndentChangesTracker from "../../IndentChangesTracker";
import axios from "axios";
import {
  fetchDraftItems,
  fetchCategories,
  fetchChangeLogs,
  saveChangeLog,
  saveDraft,
  submitIndent,
  addItem,
  deleteItem,
  updateItem,
  setError,
} from "../../../../store/invoiceSlice";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
// Separate LoadingSkeleton into its own component file
const LoadingSkeleton = React.memo(() => (
  <div className="animate-pulse space-y-4">
    <div className="h-4 bg-gray-200 rounded w-3/4" />
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
    </div>
  </div>
));

// Custom hook for API data fetching
const useIndentData = (indentId, apiEndpoint) => {
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!indentId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [indentResponse, itemsResponse] = await Promise.all([
          axios.get(apiEndpoint),
          axios.get(`${API_BASE_URL}/viewTable/indentitems/${indentId}`)
        ]);

        const indentData = Array.isArray(indentResponse.data)
          ? indentResponse.data[0]
          : indentResponse.data;

        if (indentData) {
          setPageData({
            ...indentData,
            IndentItems: itemsResponse.data || []
          });
        }
      } catch (err) {
        setError(err.message);
        setPageData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [indentId, apiEndpoint]);

  return { pageData, isLoading, error };
};

// Custom hook for managing items
const useItemsManagement = (indentId, currentUserId, items) => {
  const dispatch = useDispatch();

  const handleItemUpdate = useCallback(
    (updateInfo) => {
      const { type, item, index } = updateInfo;
      const actions = {
        add: () => {
          dispatch(addItem(item));
          dispatch(saveChangeLog({
            indentId,
            userId: currentUserId,
            type: "add",
            item
          }));
        },
        delete: () => {
          dispatch(deleteItem(index));
          dispatch(saveChangeLog({
            indentId,
            userId: currentUserId,
            type: "delete",
            item
          }));
        },
        update: () => {
          dispatch(updateItem({ index, item }));
          dispatch(saveChangeLog({
            indentId,
            userId: currentUserId,
            type: "update",
            original: items[index],
            updated: item
          }));
        }
      };

      actions[type]?.();
    },
    [dispatch, indentId, currentUserId, items]
  );

  return { handleItemUpdate };
};

const InvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { row: initialRow } = location.state || {};
  const [row, setRow] = useState(initialRow);

  const {
    items,
    categories,
    changeLogs: storedChangeLogs,
    indentStatus,
    isDraft,
    isEditable,
    loading,
    error,
    changeLog,
  } = useSelector((state) => state.invoice);

  const currentUserId = useSelector((state) => state.auth.id);
  const apiEndpoint = `${API_BASE_URL}/l2page/indents/${row?.IndentID}`;
  
  const { pageData, isLoading: pageDataLoading, error: pageError } = useIndentData(
    row?.IndentID,
    apiEndpoint
  );

  const { handleItemUpdate } = useItemsManagement(row?.IndentID, currentUserId, items);

  // Validation helper
  const validateItem = useCallback((item) => {
    if (!item.itemID || item.quantity <= 0 || item.stock < 0) {
      throw new Error("Invalid item details");
    }
    return true;
  }, []);

  const handleAddItem = useCallback(
    (newItem) => {
      try {
        validateItem(newItem);
        const itemWithDefaults = {
          ...newItem,
          itemName: newItem.itemName || "Unnamed Item",
        };
        dispatch(addItem(itemWithDefaults));
      } catch (err) {
        dispatch(setError(err.message));
      }
    },
    [dispatch, validateItem]
  );

  const handleSaveDraft = useCallback(async () => {
    if (!row?.IndentID) {
      dispatch(setError("No indent selected"));
      return;
    }

    if (!items.length) {
      dispatch(setError("Please add items before saving draft"));
      return;
    }

    try {
      await dispatch(saveDraft({ indentId: row.IndentID, items })).unwrap();
      navigate("/indent", { state: { message: "Draft saved successfully" } });
    } catch (err) {
      dispatch(setError("Failed to save draft. Please try again."));
    }
  }, [dispatch, row?.IndentID, items, navigate]);

  const handleSubmit = useCallback(async () => {
    if (!items.length) {
      dispatch(setError("Cannot submit an empty indent"));
      return;
    }

    try {
      const result = await dispatch(
        submitIndent({
          indentId: row.IndentID,
          userId: currentUserId,
          items,
        })
      ).unwrap();

      navigate("/peindent", {
        state: {
          message: `Indent submitted successfully. ${result.itemsInserted} items processed.`,
        },
      });
    } catch (err) {
      dispatch(setError(err.message || "Submission failed. Please try again."));
    }
  }, [dispatch, row?.IndentID, currentUserId, items, navigate]);

  // Initial data fetching
  useEffect(() => {
    if (row?.IndentID) {
      dispatch(fetchDraftItems(row.IndentID));
      dispatch(fetchChangeLogs(row.IndentID));
      dispatch(fetchCategories());
    }
  }, [dispatch, row?.IndentID]);

  // Memoized total amount calculation
  const totalAmount = useMemo(
    () => items.reduce((acc, item) => acc + (item.total || 0), 0).toFixed(2),
    [items]
  );

  if (loading || pageDataLoading) {
    return (
      <div className="container mx-auto p-6">
        <Header />
        <LoadingSkeleton />
      </div>
    );
  }

  const displayError = error || pageError;

  return (
    <div>
      <Header
        exportEndpoint={apiEndpoint}
        importEndpoint={apiEndpoint}
        currentIndentData={pageData}
      />
      <div className="container mx-auto p-6">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Indent Request</h3>
        </div>
        {row && (
          <>
            {displayError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {displayError}
              </div>
            )}
            <IndentDetails row={row} userId={currentUserId} />
            <ItemsTable
              indentID={row.IndentID}
              items={items}
              onAddItem={handleAddItem}
              onDeleteItem={(index) => dispatch(deleteItem(index))}
              onUpdateItem={handleItemUpdate}
              calculateTotalAmount={() => totalAmount}
              isDraft={isDraft}
              onSubmit={handleSubmit}
              onSaveDraft={handleSaveDraft}
              onPrint={() => window.print()}
              isEditable={isEditable}
              indentStatus={indentStatus}
            />
            <IndentChangesTracker
              indentID={row.IndentID.toString()}
              changes={changeLog}
              storedChangeLogs={storedChangeLogs}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(InvoicingPage);